import { usePageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Heart, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api, errMsg } from "@/lib/api";

const FavItem = ({ item, onRemove }) => {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(item.text);
      setCopied(true);
      toast.success("Copied! Paste it into your chat.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.message("Clipboard blocked — select the text and copy it manually.");
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/favorites/${item.id}`);
      onRemove(item.id);
    } catch (e) {
      toast.error(errMsg(e, "Couldn't remove it."));
      setBusy(false);
    }
  };
  return (
    <li data-testid={`favorite-item-${item.id}`} className="panel p-5 rise">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-widest font-bold text-accent">{item.label || "Saved reply"}</span>
        <span className="text-[11px] text-muted-foreground">{new Date(item.created_at).toLocaleDateString()}</span>
      </div>
      <p data-testid="favorite-item-text" translate="no" className="notranslate text-base leading-snug font-medium whitespace-pre-wrap select-text">{item.text}</p>
      {item.why && <p className="mt-2 text-xs text-muted-foreground">{item.why}</p>}
      <div className="mt-4 flex gap-2">
        <button data-testid="favorite-copy-button" onClick={copy} className="h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 brand-gradient text-white">
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}{copied ? "Copied" : "Copy"}
        </button>
        <button data-testid="favorite-remove-button" onClick={remove} disabled={busy} className="h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 border border-border hover:border-rose-500/60 disabled:opacity-40">
          <Trash2 className="h-3.5 w-3.5" />Remove
        </button>
      </div>
    </li>
  );
};

export default function Favorites() {
  usePageMeta({ title: "Favourites" });
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/favorites").then(({ data }) => setItems(data.items)).catch((e) => setError(errMsg(e, "Couldn't load favourites.")));
  }, []);
  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="favorites-page">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><Heart className="h-7 w-7 fill-accent text-accent" />Favourites</h1>
          <p className="text-sm text-muted-foreground mt-2">Your best lines, saved for later. Only you can see these.</p>
        </div>
        {error && <p data-testid="favorites-error" className="text-sm text-rose-400">{error}</p>}
        {!items && !error && <Loader2 data-testid="favorites-loading" className="h-5 w-5 animate-spin text-primary" />}
        {items?.length === 0 && (
          <div className="panel p-8 text-center space-y-3" data-testid="favorites-empty">
            <p className="text-sm text-muted-foreground">No favourites yet. Tap the heart on any reply to keep it here.</p>
            <Link to="/app" data-testid="favorites-go-generate" className="inline-flex h-10 items-center rounded-full px-5 text-sm font-semibold brand-gradient text-white">Generate replies</Link>
          </div>
        )}
        {items?.length > 0 && (
          <ul className="space-y-3" data-testid="favorites-list">
            {items.map((it) => <FavItem key={it.id} item={it} onRemove={(id) => setItems((xs) => xs.filter((x) => x.id !== id))} />)}
          </ul>
        )}
      </main>
    </div>
  );
}
